/* DuoPaw draft: one cart reconciliation path for included gifts and protection. */
(() => {
  'use strict';

  // Line keys, rather than variant IDs, also collapse split lines with properties.
  function plan(cart, config) {
    const eligible = cart.items.reduce((sum, item) =>
      sum + (String(item.product_id) === String(config.product) ? item.quantity : 0), 0);
    const updates = {};
    for (const [variant, required] of [
      [config.gift1, eligible >= 1 ? 1 : 0],
      [config.gift2, eligible >= 2 ? 1 : 0],
      [config.protection, eligible >= 1 ? null : 0]
    ]) {
      const lines = cart.items.filter(item => String(item.variant_id) === String(variant));
      const target = required === null ? (lines.length ? 1 : 0) : required;
      if (!lines.length && target) updates[variant] = target;
      lines.forEach((line, index) => {
        const quantity = index === 0 ? target : 0;
        if (line.quantity !== quantity) updates[line.key] = quantity;
      });
    }
    return updates;
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { plan };
    return;
  }

  const script = document.currentScript;
  const config = JSON.parse(script.dataset.cartRules);
  const nativeFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  let queue = Promise.resolve();
  let quantityChangedAt = 0;
  let checkingOut = false;

  function enqueue(task) {
    const result = queue.then(task);
    queue = result.catch(() => {});
    return result;
  }

  async function json(url, options) {
    const response = await nativeFetch(url, options);
    if (!response.ok) throw new Error('Cart request failed: ' + response.status);
    return response.json();
  }

  async function reconcile(sections = [], sectionsUrl = location.pathname) {
    let cart = await json(root + 'cart.js', { cache: 'no-store', credentials: 'same-origin' });
    const updates = plan(cart, config);
    if (!Object.keys(updates).length) return { cart, changed: false };
    cart = await json(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates, sections, sections_url: sectionsUrl })
    });
    if (Object.keys(plan(cart, config)).length) throw new Error('Cart rules could not be applied');
    return { cart, changed: true };
  }

  function bodyOptions(body) {
    if (body instanceof FormData || body instanceof URLSearchParams) {
      return { sections: body.get('sections'), sections_url: body.get('sections_url') };
    }
    if (typeof body === 'string') {
      try { return JSON.parse(body); } catch (_) { return {}; }
    }
    return {};
  }

  // Let the theme render only the final, reconciled sections. Original errors pass through.
  window.fetch = function(input, init) {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
    const method = (init?.method || input?.method || 'GET').toUpperCase();
    if (url.origin !== location.origin || method !== 'POST' ||
        !/\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname)) {
      return nativeFetch(input, init);
    }
    return enqueue(async () => {
      const response = await nativeFetch(input, init);
      if (!response.ok) return response;
      let original;
      try { original = await response.clone().json(); } catch (_) { return response; }
      if (original.status || original.errors) return response;
      const options = bodyOptions(init?.body);
      const sections = original.sections ? Object.keys(original.sections) :
        (Array.isArray(options.sections) ? options.sections : String(options.sections || '').split(',').filter(Boolean));
      try {
        const result = await reconcile(sections, options.sections_url || location.pathname);
        if (!result.changed) return response;
        const body = /\/add(?:\.js)?$/.test(url.pathname)
          ? { ...original, sections: result.cart.sections || original.sections }
          : result.cart;
        return new Response(JSON.stringify(body), {
          status: response.status,
          headers: { 'Content-Type': 'application/json; charset=utf-8' }
        });
      } catch (error) {
        console.error('[DuoPaw cart]', error);
        return new Response(JSON.stringify({
          status: 503, errors: 'Unable to verify your cart. Please try again.',
          description: 'Unable to verify your cart. Please try again.'
        }), { status: 503, headers: { 'Content-Type': 'application/json' } });
      }
    });
  };

  function sectionTargets() {
    const targets = [
      { id: 'cart-drawer', selector: '#CartDrawer', section: 'cart-drawer' },
      { id: 'cart-icon-bubble', selector: '#cart-icon-bubble', section: 'cart-icon-bubble' }
    ];
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const element = document.getElementById(id);
      if (element?.dataset.id) targets.push({ id, selector: '#' + id, section: element.dataset.id });
    }
    return targets;
  }

  async function refresh(cart) {
    const targets = sectionTargets();
    const url = new URL(location.href);
    url.searchParams.set('sections', targets.map(target => target.section).join(','));
    const sections = await json(url.href);
    for (const target of targets) {
      if (!sections[target.section]) continue;
      const parsed = new DOMParser().parseFromString(sections[target.section], 'text/html');
      // The section endpoint wraps the cart icon without its header anchor.
      const source = parsed.querySelector(target.selector) ||
        (target.id === 'cart-icon-bubble' ? parsed.querySelector('.shopify-section') : null);
      const current = document.querySelector(target.selector);
      if (source && current) current.innerHTML = source.innerHTML;
    }
    document.querySelectorAll('cart-drawer, cart-items, #main-cart-footer').forEach(element =>
      element.classList.toggle('is-empty', cart.item_count === 0));
  }

  function showError() {
    let element = document.getElementById('duopaw-cart-error');
    if (!element) {
      element = document.createElement('p');
      element.id = 'duopaw-cart-error';
      element.setAttribute('role', 'alert');
      (document.querySelector('.drawer.active .drawer__footer') ||
        document.querySelector('main') || document.body).append(element);
    }
    element.textContent = 'We could not verify your cart. Please try checkout again.';
  }

  async function checkout() {
    if (checkingOut) return;
    checkingOut = true;
    try {
      // The supplied theme debounces quantity fields; wait for that update before checkout.
      const remaining = 600 - (Date.now() - quantityChangedAt);
      if (remaining > 0) await new Promise(resolve => setTimeout(resolve, remaining));
      const result = await enqueue(() => reconcile());
      if (result.cart.items.some(item => String(item.product_id) === String(config.product))) {
        location.assign(root + 'checkout');
      } else {
        await refresh(result.cart);
        checkingOut = false;
      }
    } catch (error) {
      console.error('[DuoPaw checkout]', error);
      showError();
      checkingOut = false;
    }
  }

  document.addEventListener('change', event => {
    if (event.target.matches('cart-items input[name="updates[]"], cart-drawer-items input[name="updates[]"]')) {
      quantityChangedAt = Date.now();
    }
  }, true);

  document.addEventListener('click', event => {
    const target = event.target.closest('button[name="checkout"], input[name="checkout"], a[href]');
    if (!target) return;
    const isCheckoutLink = target.tagName === 'A' &&
      new URL(target.href, location.href).pathname.replace(/\/$/, '').endsWith('/checkout');
    if (target.name !== 'checkout' && !isCheckoutLink) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout();
  }, true);

  document.addEventListener('submit', event => {
    if (event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout();
  }, true);

  async function restore() {
    try {
      const result = await enqueue(() => reconcile());
      if (result.changed) await refresh(result.cart);
    } catch (error) {
      console.error('[DuoPaw restore]', error);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restore, { once: true });
  else restore();
  window.addEventListener('pageshow', event => { if (event.persisted) restore(); });
})();



